<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p data-testid="personalization-text-title" class="h6 pa-0 mb-0">
        {{ $t('organization_settings.general_settings') }}
      </p>
      <p class="mt-2 pa-0 grey--text text--darken-2">
        {{ $t('organization_settings.general_settings_subtitle') }}
      </p>
    </div>
    <v-form v-model="isFormValid" ref="form">
      <v-text-field
        required
        outlined
        dense
        data-private
        class="mb-2"
        data-testid="personalization-textField-organizationName"
        v-model="organization.name"
        :maxlength="nameMaxLength"
        :rules="[
          name =>
            !!name ||
            $t('organization_settings.your_organization_needs_to_have_a_name'),
        ]"
        :label="`${$t('organization.organization_name')}*`"
      ></v-text-field>
      <v-select
        outlined
        required
        dense
        item-text="name"
        class="mb-2"
        v-model="organization.language"
        :rules="[v => !!v || $t('validation.form.this_field_is_required')]"
        :label="`${$t('menu.language')}*`"
        :items="languages"
      ></v-select>
      <div v-if="isCustomNamesEnabled">
        <div class="mb-4">
          <p class="h6 mb-3 mt-0 text-ui-800">
            {{ $t('organization.custom_rev_name_title') }}
          </p>
          <p class="pa-0 grey--text text--darken-2">
            {{ $t('organization.custom_rev_name_description') }}
          </p>
        </div>
        <div v-if="organization.featuresEnabled__quickAction" class="mb-3">
          <p class="mb-4 overline grey--text text--darken-1">
            {{ $t('common.quick_action_one') }}
          </p>

          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="6" class="pl-0">
                <v-text-field
                  v-model="organization.customNamings.quickAction.singular"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :label="$t('organization.custom_singular_name')"
                  :maxlength="nameMaxLength"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.quickAction.plural
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="6" class="pr-0">
                <v-text-field
                  v-model="organization.customNamings.quickAction.plural"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :label="$t('organization.custom_plural_name')"
                  :maxlength="nameMaxLength"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.quickAction.singular
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </div>
        <div v-if="organization.featuresEnabled__improvementIdea" class="mb-3">
          <p class="mb-4 overline grey--text text--darken-1">
            {{ $t('common.improvement_idea_one') }}
          </p>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="6" class="pl-0">
                <v-text-field
                  v-model="organization.customNamings.improvementIdea.singular"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :maxlength="nameMaxLength"
                  :label="$t('organization.custom_singular_name')"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.improvementIdea.plural
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="6" class="pr-0">
                <v-text-field
                  v-model="organization.customNamings.improvementIdea.plural"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :maxlength="nameMaxLength"
                  :label="$t('organization.custom_plural_name')"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.improvementIdea.singular
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </div>
        <div v-if="organization.featuresEnabled__problemSolving" class="mb-3">
          <p class="mb-4 overline grey--text text--darken-1">
            {{ $t('common.problem_solving_one') }}
          </p>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="6" class="pl-0">
                <v-text-field
                  v-model="organization.customNamings.problemSolving.singular"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :maxlength="nameMaxLength"
                  :label="$t('organization.custom_singular_name')"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.problemSolving.plural
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="6" class="pr-0">
                <v-text-field
                  v-model="organization.customNamings.problemSolving.plural"
                  class="mb-2"
                  data-private
                  dense
                  outlined
                  :maxlength="nameMaxLength"
                  :label="$t('organization.custom_plural_name')"
                  :rules="[
                    val =>
                      validateCustomRevName(
                        val,
                        organization.customNamings.problemSolving.singular
                      ),
                  ]"
                  @input="validateForm"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </div>

      <p class="h6 mb-3 mt-0 text-ui-800">{{ $t('organization.logos') }}</p>
      <v-row>
        <v-col>
          <p class="mb-3 caption grey--text text--darken-1">
            {{ $t('organization.web_logo') }}
          </p>
          <ThumbnailPhotoSelector
            data-testid="logo-uploader"
            data-private
            isPublic
            isEditMode
            :mediaObject="logo"
            :disabled="false"
            @onDoneUploading="onDoneUploading($event, 'logo')"
            @onRemovePhoto="removeLogo('logo')"
          />
          <p v-if="!logo" class="font-size-10 grey--text text--darken-1 ma-0">
            {{ $t('organization.logo_size') }}
          </p>
        </v-col>
        <v-col>
          <p class="mb-3 caption grey--text text--darken-1">
            {{ $t('organization.mobile_logo') }}
          </p>
          <ThumbnailPhotoSelector
            data-testid="mobileLogo-uploader"
            data-private
            isPublic
            isEditMode
            :mediaObject="mobileLogo"
            :disabled="false"
            @onDoneUploading="onDoneUploading($event, 'mobileLogo')"
            @onRemovePhoto="removeLogo('mobileLogo')"
          />
          <p
            v-if="!mobileLogo"
            class="font-size-10 grey--text text--darken-1 ma-0"
          >
            {{ $t('organization.mobileLogo_size') }}
          </p>
        </v-col>
      </v-row>
    </v-form>
    <div class="mt-9 text-right">
      <Button
        size="large"
        color="grey"
        data-testid="personalization-button-reset"
        class="me-2"
        outlined
        :disabled="isLoading || !organizationChanged"
        :text="$t('common.button.discard_changes')"
        @click="reset"
      />
      <Button
        size="large"
        color="primary"
        data-testid="personalization-button-save"
        :loading="isLoading"
        :text="$t('common.button.save')"
        :disabled="canSave"
        @click="$emit('update')"
      />
    </div>
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import ThumbnailPhotoSelector from '@/containers/ThumbnailPhotoSelector/ThumbnailPhotoSelector'
import Button from '@/components/UI/Button'

export default {
  name: 'General',

  mixins: [mixin],

  components: {
    ThumbnailPhotoSelector,
    Button,
  },

  props: {
    organization: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    organizationChanged: {
      type: Boolean,
      required: true,
    },
    logo: {
      type: Object,
      required: false,
      default: null,
    },
    mobileLogo: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      isFormValid: true,
      nameMaxLength: this.$constants.organization.nameMaxLength,
    }
  },

  methods: {
    validateForm() {
      this.$refs.form.validate()
    },

    validateCustomRevName(val, relatedFieldValue) {
      const fieldValue = val?.trim?.()
      const relatedFieldTrimmedValue = relatedFieldValue?.trim?.() ?? ''

      if (relatedFieldTrimmedValue && !fieldValue)
        return this.$t('validation.form.this_field_is_required')

      return true
    },

    onDoneUploading(mediaObject, type) {
      this.$emit('logoChanged', type, mediaObject)
    },

    removeLogo(fieldName) {
      this.$emit('removeLogo', fieldName)
    },

    reset() {
      this.$emit('reset')
    },
  },

  computed: {
    languages() {
      return this.getLanguages()
    },

    isCustomNamesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.customNaming)
    },

    canSave() {
      return this.isLoading || !this.organization.name || !this.isFormValid
    },
  },
}
</script>
<style lang="scss">
.personalization__logo {
  min-width: 180px;
}
</style>
